import { Children, FC, ReactNode, useEffect, useRef, useState } from 'react';
import useResolvedUserDisplayName from '@/hooks/useResolvedUserDisplayName';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import ChatHeader, { type ChatHeaderProps } from '../ChatHeader/ChatHeader';
import ChatConversation from '../ChatConversation/ChatConversation';
import ChatEmptyState from '../ChatEmptyState/ChatEmptyState';
import ChatInput, { type ChatInputProps } from '../ChatInput/ChatInput';
import ChatDisclaimer from '../ChatDisclaimer/ChatDisclaimer';
import ChatWelcomeMessage, { type ChatWelcomeMessageProps } from '../ChatWelcomeMessage/ChatWelcomeMessage';
import FirstTimeDisclaimer from '../FirstTimeDisclaimer/FirstTimeDisclaimer';
import { TestIds } from './testIds';
import styles from './ChatLayout.module.css';

const isStoredBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

const readSessionDisclaimerAcceptance = (storageKey: string): boolean => {
  try {
    const storedValue = sessionStorage.getItem(storageKey);

    if (storedValue === null) {
      return false;
    }

    const parsedValue: unknown = JSON.parse(storedValue);

    if (isStoredBoolean(parsedValue)) {
      return parsedValue;
    }

    sessionStorage.removeItem(storageKey);

    return false;
  } catch {
    sessionStorage.removeItem(storageKey);

    return false;
  }
};

export interface ChatLayoutProps {
  headerProps: ChatHeaderProps;
  inputProps: Omit<ChatInputProps, 'drawerRef'>;
  bottomSlot?: ReactNode;
  filterHeaderSlot?: ReactNode;
  children: ReactNode;
  userName?: string;
  disclaimerAccepted?: boolean;
  onDisclaimerAccept?: VoidFunction;
  disclaimerStorageKey?: string;
  welcomeMessageProps?: ChatWelcomeMessageProps;
  onLearnMoreClick?: () => void;
}

const ChatLayout: FC<ChatLayoutProps> = ({
  headerProps,
  inputProps,
  bottomSlot,
  filterHeaderSlot,
  children,
  userName,
  disclaimerAccepted = true,
  onDisclaimerAccept,
  disclaimerStorageKey,
  welcomeMessageProps,
  onLearnMoreClick,
}) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const [sessionDisclaimerAccepted, setSessionDisclaimerAccepted] = useState<boolean>(() =>
    disclaimerStorageKey ? readSessionDisclaimerAcceptance(disclaimerStorageKey) : false,
  );
  const hasMessages = Children.count(children) > 0;
  const resolvedUserDisplayName = useResolvedUserDisplayName();
  const displayName = userName ?? resolvedUserDisplayName;

  useEffect(() => {
    if (!disclaimerStorageKey) {
      return;
    }

    setSessionDisclaimerAccepted(readSessionDisclaimerAcceptance(disclaimerStorageKey));
  }, [disclaimerStorageKey]);

  const shouldUseSessionDisclaimerState = Boolean(disclaimerStorageKey);
  const isDisclaimerAccepted = shouldUseSessionDisclaimerState ? sessionDisclaimerAccepted : disclaimerAccepted;
  const shouldShowWelcomeState = !hasMessages && Boolean(welcomeMessageProps);

  const handleDisclaimerAccept = () => {
    if (disclaimerStorageKey) {
      sessionStorage.setItem(disclaimerStorageKey, JSON.stringify(true));
      setSessionDisclaimerAccepted(true);
    }

    onDisclaimerAccept?.();
  };

  if (!isDisclaimerAccepted) {
    return (
      <div ref={layoutRef} className={styles.layout} data-testid={TestIds.layout}>
        <FirstTimeDisclaimer onAccept={handleDisclaimerAccept} onCancel={headerProps.onClose} />
      </div>
    );
  }

  return (
    <div ref={layoutRef} className={styles.layout} data-testid={TestIds.layout}>
      {hasMessages ? (
        <>
          <ChatHeader {...headerProps} />
          {filterHeaderSlot}
          <ChatConversation>{children}</ChatConversation>
        </>
      ) : shouldShowWelcomeState && welcomeMessageProps ? (
        <>
          <ChatHeader
            {...headerProps}
            showNewChat={Boolean(headerProps.title)}
            showTitle={Boolean(headerProps.title)}
          />
          <ScrollArea scrollbars="y" className={styles.welcomeStateSection}>
            <div className={styles.welcomeHeroSection}>
              <ChatEmptyState userName={displayName} />
            </div>
            <div className={styles.welcomeSection} data-testid={TestIds.welcomeSection}>
              <ChatWelcomeMessage {...welcomeMessageProps} />
            </div>
          </ScrollArea>
        </>
      ) : (
        <div className={styles.emptyStateSection}>
          <ChatEmptyState userName={displayName} />
        </div>
      )}

      <div className={styles.bottomSection} data-testid={TestIds.bottomSection}>
        {bottomSlot}
        <ChatInput {...inputProps} drawerRef={layoutRef} />
        <ChatDisclaimer onLearnMoreClick={onLearnMoreClick} />
      </div>
    </div>
  );
};

export default ChatLayout;
